import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

import { isTokenExpired } from '../../shared/utils/tokenExpiration'

export const GetPersonalAccessTokens = graphql(/* GraphQL */ `
  query getPersonalAccessTokens {
    personalAccessTokens {
      accessTokens {
        accessToken
        clientId
        name
        generatedAt
        expiresAt
        scopes
      }
      maxTokens
      canUpgradeForMore
    }
  }
`)

export type PersonalAccessToken = ResultOf<
  typeof GetPersonalAccessTokens
>['personalAccessTokens']['accessTokens'][number]

type UsePersonalAccessTokensResult = {
  tokens: PersonalAccessToken[]
  maxTokens: number
  canUpgradeForMore: boolean
  isLoading: boolean
  hasError: boolean
  hasExpiredToken: boolean
  refetch: () => Promise<unknown>
}

/**
 * Custom hook to query the user's personal access tokens.
 */
export const usePersonalAccessTokens = (): UsePersonalAccessTokensResult => {
  const { data, loading, error, refetch } = useQuery(GetPersonalAccessTokens)

  const tokens = data?.personalAccessTokens.accessTokens ?? []
  const maxTokens = data?.personalAccessTokens.maxTokens ?? 1
  const canUpgradeForMore = data?.personalAccessTokens.canUpgradeForMore ?? true

  const hasExpiredToken = tokens.some((token) =>
    isTokenExpired(token.expiresAt),
  )

  return {
    tokens,
    maxTokens,
    canUpgradeForMore,
    isLoading: loading,
    hasError: !!error,
    hasExpiredToken,
    refetch,
  }
}
